<template lang="pug">
  div(class="ai-generation-loader")
    div(class="d-flex align-center justify-center mb-4")
      img(
        src="~/assets/images/stars.gif"
        alt="stars"
        class="ai-generation-loader__emoji"
      )
    h2(class="title-32px bold mb-2") {{ $t('Template creation with AI') }}
    div(class="text-14px") {{ currentStepText }}
    div(class="mt-8")
      v-progress-linear(
        :value="progressValue"
        height="8"
        color="primary"
        background-color="#F1F1F1"
        rounded
      )
</template>

<script>
export default {
  name: 'BlockAiGenerationLoader',
  props: {
    onFirstRequest: {
      type: Function,
      default: null
    },
    onFinalRequest: {
      type: Function,
      default: null
    },
    closeModal: {
      type: Function,
      default: null
    },
    rejectModal: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      currentStep: 0,
      progressValue: 0,
      progressInterval: null,
      stepTimeout: null
    }
  },
  computed: {
    steps() {
      return [
        'Analyzing document structure...',
        'Identifying fields to fill...',
        'Creating dynamic fields...',
        'Finalizing generation...'
      ]
    },
    stepDurations() {
      return [1000, 1000, 1000, 1000]
    },
    totalDuration() {
      return this.stepDurations.reduce((sum, duration) => sum + duration, 0)
    },
    currentStepText() {
      return this.$t(this.steps[this.currentStep])
    }
  },
  mounted() {
    this.startProgress()
  },
  beforeDestroy() {
    this.clearTimers()
  },
  methods: {
    startProgress() {
      this.resetProgress()
      this.startProgressTimer()
      this.runSteps()
    },

    startProgressTimer() {
      let elapsedTime = 0
      this.progressInterval = setInterval(() => {
        elapsedTime += 50
        this.progressValue = Math.min((elapsedTime / this.totalDuration) * 100, 100)
      }, 50)
    },

    async runSteps() {
      try {
        for (let i = 0; i < this.steps.length; i++) {
          await this.executeStep(i)
        }
        this.completeProgress()
      } catch (error) {
        this.handleError(error)
      }
    },

    async executeStep(stepIndex) {
      this.currentStep = stepIndex

      if (stepIndex === 0 && this.onFirstRequest) {
        await this.onFirstRequest()
      }
      if (stepIndex === 3 && this.onFinalRequest) {
        await this.onFinalRequest()
      }
      if (stepIndex < this.steps.length - 1) {
        await this.delay(this.stepDurations[stepIndex])
      }
    },

    async completeProgress() {
      await this.delay(this.stepDurations[this.stepDurations.length - 1])
      this.progressValue = 100
      this.closeDialog()
    },

    handleError(error) {
      this.clearTimers()
      this.rejectDialog(error)
    },

    delay(ms) {
      return new Promise(resolve => {
        this.stepTimeout = setTimeout(resolve, ms)
      })
    },

    resetProgress() {
      this.clearTimers()
      this.currentStep = 0
      this.progressValue = 0
    },

    clearTimers() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval)
        this.progressInterval = null
      }
      if (this.stepTimeout) {
        clearTimeout(this.stepTimeout)
        this.stepTimeout = null
      }
    },

    closeDialog() {
      this.clearTimers()
      if (this.closeModal) {
        this.closeModal()
      }
    },

    rejectDialog(error) {
      if (this.rejectModal) {
        this.rejectModal(error)
      } else {
        this.closeDialog()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.ai-generation-loader {
  padding: 40px;
  &__emoji {
    width: 80px;
    height: 80px;
    object-fit: contain;
  }
}
</style>
